import { InvalidateQueryFilters, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Middleware, MutationHook } from 'react-query-kit';

import { ErrorResponse } from 'api';

type QueryFiltersOrCallback<TData, TVariables> =
  | InvalidateQueryFilters[]
  | ((data: TData, variables: TVariables, context: unknown) => InvalidateQueryFilters[]);

/**
 * Creates a middleware that invalidates specified query keys after successful mutation.
 * Supports both static query filters and dynamic query filters derived from mutation results.
 *
 * @param queryFilters - Query filters to invalidate, or a callback function that returns query filters
 * @returns Middleware that invalidates queries on mutation success
 *
 * @example
 * // Static: Single query key
 * const useSendOnboarding = createMutation({
 *   mutationFn: sendOnboarding,
 *   use: [invalidateQueryKeys([{ queryKey: ['student_profile'] }])],
 * });
 *
 * @example
 * // Static: Multiple query keys
 * const useUpdateStudent = createMutation({
 *   mutationFn: updateStudent,
 *   use: [
 *     invalidateQueryKeys([
 *       { queryKey: ['student_profile'] },
 *       { queryKey: ['students'] },
 *     ]),
 *   ],
 * });
 *
 * @example
 * // Dynamic: Query keys from mutation variables
 * const useDeletePost = createMutation({
 *   mutationFn: deletePost,
 *   use: [
 *     invalidateQueryKeys<TData, TVariables>((data, variables) => [
 *       { queryKey: ['post', variables.postId] },
 *       { queryKey: ['posts'] },
 *     ]),
 *   ],
 * });
 *
 * @example
 * // Dynamic: Query keys from mutation response data
 * const useCreateUser = createMutation({
 *   mutationFn: createUser,
 *   use: [
 *     invalidateQueryKeys<TData, TVariables>((data) => [
 *       { queryKey: ['user', data.userId] },
 *       { queryKey: ['users'] },
 *     ]),
 *   ],
 * });
 */
export function invalidateQueryKeys<TData, TVariables>(
  queryFilters: QueryFiltersOrCallback<TData, TVariables>,
): Middleware<MutationHook<TData, TVariables, AxiosError<ErrorResponse>>> {
  return (useMutationNext) => {
    return (options) => {
      const queryClient = useQueryClient();
      return useMutationNext({
        ...options,
        onSuccess: async (data, variables, onMutateResult, context) => {
          // Resolve query filters (static or from callback)
          const filters = typeof queryFilters === 'function' ? queryFilters(data, variables, context) : queryFilters;

          // Invalidate all specified queries
          await Promise.all(filters.map((filter) => queryClient.invalidateQueries(filter)));

          // Call the original onSuccess callback if provided
          options.onSuccess?.(data, variables, onMutateResult, context);
        },
      });
    };
  };
}
